/**
 * Left-rail navigation for the Build hub.
 *
 * Ports the structural shell of `fe-prototype/build/Setup.jsx`'s sidebar —
 * a business header at the top (logo + name + vertical), a stack of
 * `nav-item` buttons, and `nav-sep` dividers between logical groups.
 *
 * Each nav entry declares its `id`, `icon`, `label`, an `enabled` flag,
 * and (optionally) a small `badge`. Disabled items still render — they
 * just don't fire `onSelect` and get a "Coming next" pill on the right.
 * This is intentional: the prototype's nav silhouette is what gives the
 * visitor a sense of what's coming in Phase 3, even though only Overview
 * / Knowledge base / Test & preview are wired in Phase 2.
 */

import { Icon } from "./Icon.js";

/** A navigation entry — either a real item or a `sep` rule between groups. */
export type NavItem =
  | {
      id: string;
      icon: string;
      label: string;
      enabled: boolean;
      badge?: { text: string; cls?: "" | "ok" | "add" | "" };
    }
  | { sep: true };

interface HubNavProps {
  businessName: string;
  businessType: string;
  items: NavItem[];
  active: string;
  onSelect: (id: string) => void;
}

/**
 * Sidebar of the Build hub. `onSelect` only fires for entries with
 * `enabled: true`; disabled entries render dimmed with a "Coming next"
 * badge but stay inert.
 */
export function HubNav({
  businessName,
  businessType,
  items,
  active,
  onSelect,
}: HubNavProps) {
  return (
    <aside className="hub-nav" data-testid="hub-nav">
      <div className="hub-biz">
        <img src="/img/offsider-monogram.svg" alt="" />
        <div>
          <div className="hb-n">{businessName}</div>
          <div className="hb-s">{businessType}</div>
        </div>
      </div>
      {items.map((n, i) => {
        if ("sep" in n) {
          return <div key={`sep-${i}`} className="nav-sep" />;
        }
        const isActive = active === n.id;
        const isDisabled = !n.enabled;
        const className =
          "nav-item" +
          (isActive ? " active" : "") +
          (isDisabled ? " disabled" : "");
        return (
          <button
            key={n.id}
            type="button"
            className={className}
            onClick={() => {
              if (n.enabled) onSelect(n.id);
            }}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            data-testid={`hub-nav-${n.id}`}
          >
            <span className="ni-ico">
              <Icon name={n.icon} size={17} />
            </span>
            {n.label}
            {n.badge && (
              <span className={"ni-badge " + (n.badge.cls ?? "")}>
                {n.badge.text}
              </span>
            )}
            {isDisabled && !n.badge && (
              <span className="ni-badge">Coming next</span>
            )}
          </button>
        );
      })}
    </aside>
  );
}
